import React from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "@reduxjs/toolkit";
import {
  Building,
  Mail,
  MapPin,
  Phone,
  UserRound,
  Pencil,
} from "lucide-react";

import { getDetailContact } from "slices/thunk";
import avatar from "assets/images/users/user-2.jpg";

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "components/ui/sheet";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "components/ui/avatar";
import { Button } from "components/ui/button";
import { ScrollArea } from "components/ui/scroll-area";
import { Separator } from "components/ui/separator";

interface ContactInfoPanelProps {
  show: boolean;
  onHide: () => void;
  chatDetail: any;
  contactDetail: any;
  contactId: string;
}

const initials = (name?: string) =>
  (name ?? "")
    .split(" ")
    .map((word: string) => word.charAt(0).toUpperCase())
    .join("")
    .slice(0, 2) || "??";

const InfoRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  value?: string;
}> = ({ icon, label, value }) => (
  <div className="flex items-center gap-3 rounded-xl border border-border/50 bg-muted/30 p-3 transition-colors hover:bg-muted/50">
    <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-card text-muted-foreground">
      {icon}
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className="truncate text-sm font-medium text-foreground">
        {value || "—"}
      </p>
    </div>
  </div>
);

const ContactInfoPanel: React.FC<ContactInfoPanelProps> = ({
  show,
  onHide,
  chatDetail,
  contactDetail,
  contactId,
}) => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  const selectContactData = createSelector(
    (state: any) => state.Contact,
    (contact) => ({
      contactDetailData: contact.contactDetail,
      error: contact.error,
    })
  );

  const { contactDetailData } = useSelector(selectContactData);

  const handleClickMoveToContactPage = () => {
    if (contactDetail.id && contactId === contactDetail.id) {
      navigate("/contact", { state: { fromPanelOpen: true } });
    } else {
      dispatch(
        getDetailContact(
          contactDetail.id !== "" ? contactDetail.id : contactId
        )
      );
      navigate("/contact", { state: { fromPanelOpen: true } });
    }
  };

  const summary = chatDetail?.conversationSummary;

  return (
    <Sheet open={show} onOpenChange={(open) => !open && onHide()}>
      <SheetContent side="right" className="w-full p-0 sm:max-w-md">
        <SheetHeader className="border-b border-border/60 px-6 py-4">
          <SheetTitle>Profile</SheetTitle>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-4rem)]">
          <div className="space-y-6 p-6">
            {/* Profile */}
            <div className="text-center">
              <Avatar className="mx-auto h-20 w-20">
                {contactDetail?.image ? (
                  <AvatarImage src={avatar} alt={contactDetail?.name} />
                ) : null}
                <AvatarFallback className="text-lg">
                  {initials(contactDetail?.name)}
                </AvatarFallback>
              </Avatar>
              <h3 className="mt-3 text-lg font-semibold text-foreground">
                {contactDetail?.name || "Unknown Contact"}
              </h3>
              {contactDetail?.company && (
                <p className="text-sm text-muted-foreground">
                  {contactDetail.company}
                </p>
              )}
            </div>

            <Separator />

            {/* Personal Information */}
            <section className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Personal Information
                </h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClickMoveToContactPage}
                >
                  <Pencil className="h-3 w-3" />
                  Edit
                </Button>
              </div>

              <div className="space-y-2">
                <InfoRow
                  icon={<UserRound className="h-4 w-4" />}
                  label="Display Name"
                  value={contactDetail?.name || contactDetailData?.name}
                />
                <InfoRow
                  icon={<UserRound className="h-4 w-4" />}
                  label="First Name"
                  value={contactDetail?.firstName || contactDetailData?.firstName}
                />
                <InfoRow
                  icon={<UserRound className="h-4 w-4" />}
                  label="Last Name"
                  value={contactDetail?.lastName || contactDetailData?.lastName}
                />
                <InfoRow
                  icon={<Mail className="h-4 w-4" />}
                  label="Email"
                  value={contactDetail?.email || contactDetailData?.email}
                />
                <InfoRow
                  icon={<Phone className="h-4 w-4" />}
                  label="Phone"
                  value={
                    contactDetail?.phoneNumber || contactDetailData?.phoneNumber
                  }
                />
                <InfoRow
                  icon={<Building className="h-4 w-4" />}
                  label="Company"
                  value={contactDetail?.company || contactDetailData?.company}
                />
                <InfoRow
                  icon={<MapPin className="h-4 w-4" />}
                  label="Address"
                  value={contactDetail?.address || contactDetailData?.address}
                />
              </div>
            </section>

            {/* About */}
            <section className="space-y-3">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                About
              </h4>
              <div className="rounded-xl border border-border/50 bg-muted/30 p-4">
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {contactDetail?.description ||
                    contactDetailData?.description ||
                    contactDetail?.bio ||
                    contactDetailData?.bio ||
                    "No description available."}
                </p>
              </div>
            </section>

            {/* Conversation Summary */}
            <section className="space-y-3">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Conversation Summary
              </h4>
              <div className="rounded-xl border border-border/50 bg-muted/30 p-4">
                <div className="text-sm leading-relaxed text-muted-foreground">
                  {summary ? (
                    summary.includes("**") ? (
                      (() => {
                        const parts = summary
                          .split("**")
                          .filter((s: string) => s.trim());
                        const sections: { header: string; content: string }[] = [];
                        for (let i = 0; i < parts.length; i += 2) {
                          const header = parts[i]?.trim() || "";
                          const content =
                            parts[i + 1]?.trim().replace(/^\*\* /, "") || "";
                          sections.push({ header, content });
                        }
                        return sections.map((section, index) => (
                          <p key={index} className="mb-2 last:mb-0">
                            <strong className="text-foreground">
                              {section.header}
                            </strong>{" "}
                            {section.content}
                          </p>
                        ));
                      })()
                    ) : (
                      summary
                    )
                  ) : (
                    "No summary available yet."
                  )}
                </div>
              </div>
            </section>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

export default ContactInfoPanel;
